import { useEffect, useState, useRef } from "react";
import api, { API } from "@/lib/api";
import { Download, Search, Trash2, Eye, X, Upload, LayoutGrid, List } from "lucide-react";
import { toast } from "sonner";
import LeadKanban from "@/components/LeadKanban";

const STATUSES = ["New", "Contacted", "Qualified", "Interested", "Follow-up", "Converted", "Closed", "Lost"];

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("table");
  const fileRef = useRef(null);

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("search", q);
    if (status) params.set("status", status);
    api.get(`/admin/leads?${params.toString()}`).then(({ data }) => setLeads(data));
  };

  useEffect(() => { load(); }, [q, status]);

  const updateStatus = async (id, newStatus) => {
    await api.patch(`/admin/leads/${id}`, { status: newStatus });
    toast.success("Status updated");
    load();
    if (selected?.id === id) setSelected({ ...selected, status: newStatus });
  };

  const remove = async (id) => {
    if (!confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    toast.success("Deleted");
    load();
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("eq_admin_token");
    const res = await fetch(`${API}/admin/leads/export/csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "leads.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    return lines.slice(1).map((line) => {
      // simple CSV split (no quoted-comma support)
      const cells = line.split(",").map((c) => c.trim());
      const obj = {};
      headers.forEach((h, i) => { obj[h] = cells[i] || ""; });
      return obj;
    });
  };

  const importCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    const rows = parseCsv(text);
    if (!rows.length) { toast.error("No rows found"); return; }
    try {
      const { data } = await api.post("/admin/leads/import", { leads: rows });
      toast.success(`Imported ${data.inserted} leads`);
      load();
    } catch { toast.error("Import failed"); }
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black text-3xl" data-testid="admin-leads-title">Lead Management</h1>
          <p className="text-neutral-500 mt-1">{leads.length} leads</p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-white border border-neutral-200 rounded-full p-1">
            <button onClick={() => setView("table")} className={`px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${view === "table" ? "bg-neutral-900 text-white" : "text-neutral-600"}`} data-testid="leads-view-table"><List className="w-3 h-3" /> Table</button>
            <button onClick={() => setView("kanban")} className={`px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${view === "kanban" ? "bg-neutral-900 text-white" : "text-neutral-600"}`} data-testid="leads-view-kanban"><LayoutGrid className="w-3 h-3" /> Kanban</button>
          </div>
          <label className="btn-outline-eq text-sm cursor-pointer" data-testid="leads-import">
            <Upload className="w-4 h-4" /> Import CSV
            <input ref={fileRef} type="file" accept=".csv" hidden onChange={importCsv} />
          </label>
          <button onClick={exportCsv} className="btn-outline-eq text-sm" data-testid="leads-export"><Download className="w-4 h-4" /> Export CSV</button>
        </div>
      </div>

      <div className="flex gap-3 mt-6 flex-wrap">
        <div className="flex-1 min-w-[240px] relative">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name/email/phone"
                 className="w-full pl-11 pr-4 py-3 rounded-full border border-neutral-200 bg-white focus:outline-none focus:border-sky-500" data-testid="leads-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-4 py-3 rounded-full border border-neutral-200 bg-white" data-testid="leads-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-neutral-200 overflow-x-auto" style={{ display: view === "table" ? "block" : "none" }}>
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">Course</th>
              <th className="text-left px-4 py-3">Source</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`lead-row-${l.id}`}>
                <td className="px-4 py-3 font-semibold">{l.name}</td>
                <td className="px-4 py-3"><div>{l.phone}</div><div className="text-neutral-500 text-xs">{l.email}</div></td>
                <td className="px-4 py-3">{l.course || "-"}</td>
                <td className="px-4 py-3"><span className="px-2 py-1 rounded bg-neutral-100 text-xs">{l.source}</span></td>
                <td className="px-4 py-3">
                  <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)}
                          className="px-2 py-1 rounded-lg border border-neutral-200 text-xs" data-testid={`lead-status-${l.id}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={() => setSelected(l)} className="p-1.5 hover:bg-neutral-100 rounded" data-testid={`lead-view-${l.id}`}><Eye className="w-4 h-4" /></button>
                  <button onClick={() => remove(l.id)} className="p-1.5 hover:bg-red-100 text-red-600 rounded" data-testid={`lead-delete-${l.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={6} className="text-center py-16 text-neutral-500">No leads yet</td></tr>}
          </tbody>
        </table>
      </div>

      {view === "kanban" && (
        <div className="mt-6" data-testid="leads-kanban-wrapper">
          <LeadKanban leads={leads} onStatusChange={updateStatus} />
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-xl">Lead Details</h3>
              <button onClick={() => setSelected(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-2 text-sm">
              <div><strong>Name:</strong> {selected.name}</div>
              <div><strong>Phone:</strong> {selected.phone}</div>
              <div><strong>Email:</strong> {selected.email || "-"}</div>
              <div><strong>Course:</strong> {selected.course || "-"}</div>
              <div><strong>City:</strong> {selected.city || "-"}</div>
              <div><strong>Source:</strong> {selected.source}</div>
              <div><strong>Status:</strong> {selected.status}</div>
              <div><strong>Message:</strong> {selected.message || "-"}</div>
              <div><strong>Created:</strong> {new Date(selected.created_at).toLocaleString()}</div>
              {selected.history?.length > 0 && (
                <div>
                  <strong>History:</strong>
                  <ul className="mt-1 text-xs text-neutral-600 list-disc list-inside">
                    {selected.history.map((h, i) => <li key={i}>{new Date(h.at).toLocaleString()} — {h.action}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
